import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { DrawOpts, EdgeModel, NodeModel, ShapeAttrs, ShapeElement } from '../../../../types';
import { shapeNames } from '../../../constants';
import {
  addTooltip,
  getAllOffsets,
  getNodeTitle,
  getOffsetsSum,
  getTextDimensions,
  getTooltip,
  optimizeMultilineText,
  removeTooltip,
  updateTooltip,
} from '../../../utils';
import { DEFAULT_TITLE_ROWS } from '../note.constants';

/* ---------------------------------- Title --------------------------------- */

interface GetNoteTextArgs extends Omit<DrawOpts, 'group'> {
  // Is the text truncated?
  isTruncated?: boolean;
  // The max width of the text - used for truncation
  maxWidth?: number;
}

export const getNoteTitleText = ({
  model,
  options,
  isTruncated = true,
  maxWidth,
  size,
}: GetNoteTextArgs): string => {
  const title = getNodeTitle(model as NodeModel, options);

  if (isTruncated) {
    const { rows: maxRows = DEFAULT_TITLE_ROWS } = options[shapeNames.title] || {};
    const [noteWidth] = size;

    return optimizeMultilineText(title, {
      maxWidth: maxWidth || noteWidth,
      maxRows,
      fontConfig: options[shapeNames.title],
    });
  }

  return title;
};

export const getTitleHeight = ({ options, model }: Pick<DrawOpts, 'options' | 'model'>) => {
  const title = getNodeTitle(model as NodeModel, options);
  if (isEmpty(title)) return 0;
  const { rows = DEFAULT_TITLE_ROWS, lineHeight } = options[shapeNames.title];
  return lineHeight * rows;
};

const getTitleAttrs = ({ options, model, size }: Omit<DrawOpts, 'group'>) => {
  const wrapperOffsets = getAllOffsets(options[shapeNames.wrapper]);
  const titleOffsets = getAllOffsets(options[shapeNames.title]);
  const [top, right, , left] = getOffsetsSum(wrapperOffsets, titleOffsets);
  const [, , , wrapperLeft] = wrapperOffsets;
  let offsetLeft = 0;

  if (model.isLinkButtonVisible) {
    const linkAttrs = options[shapeNames.linkIcon];
    const [, linkMarginRight, , linkMarginLeft] = getAllOffsets(linkAttrs);
    offsetLeft = (linkAttrs.width || 0) + linkMarginRight + linkMarginLeft;
  }

  return {
    x: wrapperLeft + offsetLeft,
    y: top + getTitleHeight({ options, model }) / 2,
    text: getNoteTitleText({ model, options, maxWidth: size[0] - left - right - offsetLeft, size }),
  };
};

export const drawTitle = ({ group, options, model, size }: DrawOpts) => {
  return group.addShape('text', {
    name: shapeNames.title,
    className: shapeNames.title,
    draggable: true,
    attrs: {
      ...getTitleAttrs({ options, model, size }),
      ...options[shapeNames.title],
    },
  });
};

export const updateTitle = ({ group, options, model, size }: DrawOpts) => {
  const { x, text } = getTitleAttrs({ options, model, size });
  group.findByClassName(shapeNames.title).attr('text', text).attr('x', x);
};

/* ---------------------------------- Body ---------------------------------- */

interface GetNoteBodyArgs extends GetNoteTextArgs {
  attrs: ShapeAttrs;
}

export const getBodyDimensions = ({
  options,
  size,
  model,
}: Pick<DrawOpts, 'options' | 'size' | 'model'>) => {
  const [noteWidth, noteHeight] = size;
  const wrapperOffsets = getAllOffsets(options[shapeNames.wrapper]);
  const bodyOffsets = getAllOffsets(options[shapeNames.body]);
  const [top, right, bottom, left] = getOffsetsSum(wrapperOffsets, bodyOffsets);
  const topWithTitle = top + getTitleHeight({ options, model });

  return {
    top: topWithTitle,
    bottom,
    left,
    right,
    height: noteHeight - topWithTitle - bottom,
    width: noteWidth - left - right,
  };
};

const getMaxBodyRows = ({ height = 1, lineHeight = 16 }: ShapeAttrs) => {
  const rows = Math.floor(height / lineHeight);

  return Math.max(1, rows);
};

const truncateBodyText = (text = '', opts: Omit<GetNoteBodyArgs, 'model' | 'size'>) => {
  const { options, attrs, isTruncated = true } = opts;
  return optimizeMultilineText(text, {
    maxWidth: attrs.width,
    maxRows: isTruncated ? getMaxBodyRows(attrs) : Infinity,
    fontConfig: options[shapeNames.body],
  });
};

export const getNoteBodyText = ({ model, options, isTruncated, attrs }: GetNoteBodyArgs) => {
  return truncateBodyText(model?.body || '', { options, attrs, isTruncated });
};

export const getNoteBodyAttrs = (args: Omit<DrawOpts, 'group'> & { isTruncated?: boolean }) => {
  const { options, isTruncated = true } = args;
  const { top, left, width, height } = getBodyDimensions(args);
  const bodyOptions = options[shapeNames.body];
  const emptyBodyOptions = options[shapeNames.emptyBody];
  // inline edit uses the height and width to calculate the textarea, so even
  // though these are not *technically* needed for the shape, we include them here
  const attrs = { ...bodyOptions, x: left, y: top, height, width };
  const modelText = getNoteBodyText({ ...args, attrs });
  const emptyBodyIsShown = isEmpty(trim(modelText));
  const text = emptyBodyIsShown
    ? truncateBodyText(emptyBodyOptions.text, { ...args, attrs })
    : modelText;

  return {
    ...attrs,
    ...(emptyBodyIsShown ? emptyBodyOptions : {}),
    text,
    height: isTruncated ? height : getTextDimensions(text, bodyOptions).height,
  };
};

const getShouldShowTooltip = (model: NodeModel | EdgeModel, attrs: ShapeAttrs) =>
  model?.showFullTextAsTooltip && !isEmpty(attrs);

export const drawBody = ({ group, options, model, size }: DrawOpts) => {
  const bodyAttrs = getNoteBodyAttrs({ options, model, size });
  const textShape = group.addShape('text', {
    name: shapeNames.body,
    className: shapeNames.body,
    draggable: true,
    attrs: bodyAttrs,
  });

  const shouldShowTooltip = getShouldShowTooltip(model, bodyAttrs);

  if (shouldShowTooltip && model?.body) {
    addTooltip({ shape: textShape, text: model.body });
  }

  return textShape;
};

export const updateBody = ({ group, options, model, size }: DrawOpts) => {
  const bodyShape = group.findByClassName(shapeNames.body) as ShapeElement;
  const attrs = getNoteBodyAttrs({ options, model, size });
  const keysToWatch = [...Object.keys(options[shapeNames.emptyBody]), 'width', 'height'];

  // Iterate over the values which could have changed
  keysToWatch.forEach((prop) => {
    if (bodyShape.attrs[prop] !== attrs[prop]) {
      bodyShape.attr(prop, attrs[prop]);
    }
  });

  const shouldShowTooltip = getShouldShowTooltip(model, attrs);

  /** We only need to continue if tooltip is enabled */
  if (!shouldShowTooltip) return;

  const tooltip = getTooltip(bodyShape);
  const tooltipExists = !isEmpty(tooltip);
  const tooltipText = model?.body ?? '';

  if (tooltipExists && model?.body) {
    // Case: `body` changed
    updateTooltip({
      shape: bodyShape,
      tooltipText,
    });
  } else if (tooltipExists && !model?.body) {
    // Case: there's no `body` to display on tooltip
    removeTooltip(bodyShape);
  } else if (!tooltipExists && model?.body) {
    // Case: `body` was empty and content has been added
    addTooltip({ shape: bodyShape, text: tooltipText });
  }
};
